/**
 * `/agents/roadmap` — the blocked-workflow catalog (RO-3-13).
 *
 * Rewrite of `excellent/app/agents/roadmap/page.tsx` (145 LOC of server JSX),
 * which RO-3-02 deferred here.
 *
 * ## What made this a REWRITE
 *
 * The page called `listBlockedLoops()` and `knownRoleSlugs()` inline, then did
 * the aggregation that gives it its reason to exist — "if I authored role X, how
 * many workflows would unblock?" — in the server render body, alongside
 * `DataTable` column definitions (functions, which cannot cross a
 * server→client boundary). Nothing about that is a mechanical port.
 *
 * ## Decision — the derived numbers arrive DERIVED
 *
 * `workflowRoadmap.get` returns `topMissing`, `knownRoleCount` and
 * `missingRoleCount` already computed. This route does no aggregation at all.
 * Two reasons, both recorded in the contract: `knownRoleSlugs()` is a DISK read
 * of `packages/agents/roles/*.md` that a browser cannot perform, and the
 * header's counts must agree with the leverage badges — deriving one on each
 * side of the wire is how those two drift apart.
 *
 * ## Decision — `<Badge>` classNames were re-checked, not copied
 *
 * The original decorated its badges with `className="font-mono"` and
 * `className="gap-2"`. A rewritten file gets an allowlist allowance of 0, so
 * every copied `className` had to be justified rather than inherited:
 * `font-mono` and `gap-2` are a font-family and a flex gap — neither is a
 * vertical-metric token (height / min-height / padding-y / leading / text-size),
 * which is what `check-button-consistency`'s Pass 3 forbids on an atom. They are
 * kept. What would NOT have survived is an `h-*`/`type-subtext` repaint like the one
 * that cost #44387 a reject; there is none here.
 */

import { createRoute } from "@tanstack/react-router";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  PageHeader,
  type DataTableColumn,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import {
  workflowRoadmap,
  type BlockedWorkflowDto,
} from "@excellent/ui/api/contracts/workflows/roadmap";
import { rootRoute } from "./__root";

const columns: DataTableColumn<BlockedWorkflowDto>[] = [
  {
    key: "loop",
    header: "workflow",
    render: (r) => (
      <Link
        href={`/agents/workflows/${r.slug}`}
        className="text-foreground hover:underline"
      >
        {r.title}
      </Link>
    ),
  },
  {
    key: "domain",
    header: "domain",
    cellClassName: "text-muted-foreground",
    render: (r) => r.domain ?? "—",
  },
  {
    key: "steps",
    header: "steps",
    align: "center",
    cellClassName: "tabular-nums text-muted-foreground",
    render: (r) => String(r.step_count),
  },
  {
    key: "missing",
    header: "missing roles",
    render: (r) => (
      <div className="flex flex-wrap gap-1">
        {r.missing_roles.map((slug) => (
          <Badge key={slug} variant="outline" className="font-mono">
            {slug}
          </Badge>
        ))}
      </div>
    ),
  },
];

function RoadmapPage() {
  const { data, error, isLoading } = useApi(workflowRoadmap.get, {});

  if (isLoading && !data) {
    return (
      <div className="px-6 py-8">
        <p className="type-body text-muted-foreground">Loading roadmap…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-8">
        <PageHeader
          title="Workflow roadmap"
          description="The roadmap could not be read."
        />
        <p className="mt-3 type-subtext text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </p>
      </div>
    );
  }

  const rows = data?.rows ?? [];
  const knownRoleCount = data?.knownRoleCount ?? 0;
  const missingRoleCount = data?.missingRoleCount ?? 0;
  const topMissing = data?.topMissing ?? [];

  return (
    <div className="flex flex-col">
      <div className="@container/page px-6 py-8">
        <PageHeader
          className="mb-5"
          title="Workflow roadmap"
          description={
            <>
              {rows.length} workflow template{rows.length === 1 ? "" : "s"} awaiting
              role authoring. {knownRoleCount} role
              {knownRoleCount === 1 ? "" : "s"} defined in
              <code className="mx-1">packages/agents/roles/</code>;
              {missingRoleCount} more needed.
            </>
          }
          toolbar={
            <Button asChild variant="outline">
              <Link href="/agents/workflows">← Fleet</Link>
            </Button>
          }
        />

        {topMissing.length > 0 ? (
          <Card className="mb-5">
            <CardHeader>
              <CardTitle className="type-title">Highest-leverage roles</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-3 type-body text-muted-foreground">
                Authoring any of these unblocks the listed number of workflows.
              </p>
              <div className="flex flex-wrap gap-2">
                {topMissing.map((m) => (
                  <Badge key={m.slug} variant="outline" className="gap-2">
                    <code className="font-mono text-foreground">{m.slug}</code>
                    <span className="text-muted-foreground">unblocks {m.unblocks}</span>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id}
          empty="Every workflow has its role chain. The fleet is the catalog."
        />
      </div>
    </div>
  );
}

export const agentsRoadmapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/roadmap",
  component: RoadmapPage,
});
